// data-fit LLC · DecisionFlow · ADMIN Stage
// ImportLogViewer.jsx · DDL: R5 · API: v1.11 · Session 94
// Auth: useAuth() direct — ARCH-2 compliant
// Key constraints: read-only except override approval; approve gate strict; reload detail after approve
// Open Items: OI-ADMIN-IMPORT-LOG-LIST, OI-ADMIN-IMPORT-LOG-GET, OI-ADMIN-IMPORT-OVERRIDE-APPROVE
// M040 C2: dark: prefix removed, hardcoded colors → tokens, text-* → text-tok-*, all <td> get text-[rgb(var(--color-text))]

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthContext.jsx'
import { ChevronUp, ChevronDown, ChevronsUpDown, Search, X } from 'lucide-react'
import { useResizablePanel } from '../../hooks/useResizablePanel.js' // S946 — resizable panel

const APPROVE_ALREADY_APPROVED_MESSAGE = 'This override has already been approved.'

const getErrorEnvelope = (err) => {
  if (err?.error) { const { code, message, http_status } = err.error; return { code, message, http_status } }
  return { code: 'UNKNOWN_ERROR', message: 'An unexpected error occurred.', http_status: null }
}

const parseApiResponse = async (response) => {
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw payload?.error ? payload : { error: { code: 'HTTP_ERROR', http_status: response.status, message: 'Request failed.' } }
  }
  return payload
}

const getRowsFromEnvelope = (payload) => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.results)) return payload.results
  return []
}

const getSingleFromEnvelope = (payload) => {
  if (payload?.data && !Array.isArray(payload.data)) return payload.data
  if (payload?.item) return payload.item
  if (payload?.result) return payload.result
  return payload
}

const getPaginationFromEnvelope = (payload) => ({
  next_cursor: payload?.pagination?.next_cursor || null,
  has_more: Boolean(payload?.pagination?.has_more),
})

export default function ImportLogViewer({ aiStatus, aiResponse, currentScreen, onNavigate }) {
  const { token } = useAuth()

  const [importLogs, setImportLogs] = useState([])
  const [selectedImportLogId, setSelectedImportLogId] = useState(null)
  const [selectedImportLog, setSelectedImportLog] = useState(null)
  const [nextCursor, setNextCursor] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isLoadingDetail, setIsLoadingDetail] = useState(false)
  const [isApproving, setIsApproving] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [detailError, setDetailError] = useState(null)
  const [approveError, setApproveError] = useState(null)
  const [confirmApproveId, setConfirmApproveId] = useState(null)

  // S946 — resizable panel
  const { panelWidth, dragHandleProps } = useResizablePanel({
    storageKey:   'df_panel_import_log',
    defaultWidth: 384,
    minWidth:     280,
    maxWidth:     800,
  })

  const loadImportLogs = useCallback(async ({ cursor = null, append = false } = {}) => {
    if (append) setIsLoadingMore(true); else setIsLoading(true)
    setErrorMessage(null)
    try {
      const queryParams = new URLSearchParams()
      if (cursor) queryParams.set('cursor', cursor)
      const url = queryParams.toString() ? `/api/v1/import-logs?${queryParams.toString()}` : '/api/v1/import-logs'
      const response = await fetch(url, { method: 'GET', credentials: 'include' })
      const payload = await parseApiResponse(response)
      const rows = getRowsFromEnvelope(payload)
      const pagination = getPaginationFromEnvelope(payload)
      setImportLogs((current) => (append ? [...current, ...rows] : rows))
      setNextCursor(pagination.next_cursor)
      setHasMore(pagination.has_more)
    } catch (err) {
      const { code, message } = getErrorEnvelope(err)
      setErrorMessage(message || `Unable to load import logs. (${code})`)
    } finally {
      setIsLoading(false); setIsLoadingMore(false)
    }
  }, [token])

  const loadImportLogDetail = useCallback(async (importLogId) => {
    if (!importLogId) return
    setIsLoadingDetail(true); setDetailError(null)
    try {
      const response = await fetch(`/api/v1/import-logs/${importLogId}`, { method: 'GET', credentials: 'include' })
      const payload = await parseApiResponse(response)
      setSelectedImportLog(getSingleFromEnvelope(payload))
    } catch (err) {
      const { code, message } = getErrorEnvelope(err)
      setDetailError(message || `Unable to load import log detail. (${code})`)
    } finally {
      setIsLoadingDetail(false)
    }
  }, [token])

  useEffect(() => { loadImportLogs() }, [loadImportLogs])
  useEffect(() => { if (!selectedImportLogId) return; loadImportLogDetail(selectedImportLogId) }, [selectedImportLogId, loadImportLogDetail])

  const handleSelectImportLog = (row) => {
    setSelectedImportLogId(row.import_log_id); setSelectedImportLog(null)
    setDetailError(null); setApproveError(null); setConfirmApproveId(null)
  }

  const canApproveSelectedLog = selectedImportLog?.closed_period_override === true && selectedImportLog?.approved_by === null

  const handleApproveOverride = async () => {
    if (!selectedImportLog || !canApproveSelectedLog) return
    if (confirmApproveId !== selectedImportLog.import_log_id) return
    setIsApproving(true); setApproveError(null)
    try {
      const response = await fetch(`/api/v1/import-logs/${selectedImportLog.import_log_id}/approve-override`, { method: 'POST', credentials: 'include', body: JSON.stringify({}) })
      await parseApiResponse(response)
      setConfirmApproveId(null)
      const reloadResponse = await fetch(`/api/v1/import-logs/${selectedImportLog.import_log_id}`, { method: 'GET', credentials: 'include' })
      const payload = await parseApiResponse(reloadResponse)
      setSelectedImportLog(getSingleFromEnvelope(payload))
    } catch (err) {
      const { code, message, http_status } = getErrorEnvelope(err)
      if (http_status === 422) setApproveError(APPROVE_ALREADY_APPROVED_MESSAGE)
      else setApproveError(message || `Unable to approve override. (${code})`)
    } finally {
      setIsApproving(false)
    }
  }

  const renderStatusBadge = (status) => {
    const colors = {
      loaded:     'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]',
      failed:     'bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))]',
      partial:    'bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))]',
      rejected:   'bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))]',
      pending:    'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]',
      validating: 'bg-[rgb(var(--color-info))]/10 text-[rgb(var(--color-info))]',
      overridden: 'bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))]',
    }
    return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-tok-xs font-medium ${colors[status] || 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]'}`}>{status || '—'}</span>
  }

  const renderBoolean = (value) => (value ? 'Yes' : 'No')

  const renderPeriodsAffected = (periodsAffected) => {
    if (!Array.isArray(periodsAffected) || periodsAffected.length === 0) return '—'
    return <ul className="list-none p-0 m-0 space-y-0.5">{periodsAffected.map((pc) => <li key={pc} className="text-tok-sm">{pc}</li>)}</ul>
  }

  // renderListRows replaced by inline sort/search render (S579)

  // renderDetailPanel and renderApprovalSection moved into side panel (S579)

  // S579 — OI-UI-TABLE-CONSISTENCY
  const [search, setSearch]   = useState('')
  const [sortCol, setSortCol] = useState('source_filename')
  const [sortDir, setSortDir] = useState('asc')

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortCol(col); setSortDir('asc') }
  }

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronsUpDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-text-muted))]" />
    return sortDir === 'asc'
      ? <ChevronUp className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />
      : <ChevronDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />
  }

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return importLogs.filter((r) =>
      !q ||
      (r.source_filename ?? '').toLowerCase().includes(q) ||
      (r.file_type ?? '').toLowerCase().includes(q) ||
      (r.source_system ?? '').toLowerCase().includes(q) ||
      (r.load_status ?? '').toLowerCase().includes(q)
    )
  }, [importLogs, search])

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av = '', bv = ''
      if (sortCol === 'source_filename')       { av = a.source_filename ?? '';  bv = b.source_filename ?? '' }
      else if (sortCol === 'file_type')        { av = a.file_type ?? '';        bv = b.file_type ?? '' }
      else if (sortCol === 'source_system')    { av = a.source_system ?? '';    bv = b.source_system ?? '' }
      else if (sortCol === 'load_status')      { av = a.load_status ?? '';      bv = b.load_status ?? '' }
      else if (sortCol === 'rows_received')    { av = a.rows_received ?? 0;     bv = b.rows_received ?? 0 }
      else if (sortCol === 'rows_loaded')      { av = a.rows_loaded ?? 0;       bv = b.rows_loaded ?? 0 }
      else if (sortCol === 'rows_rejected')    { av = a.rows_rejected ?? 0;     bv = b.rows_rejected ?? 0 }
      else if (sortCol === 'closed_override')  { av = a.closed_period_override ? '1' : '0'; bv = b.closed_period_override ? '1' : '0' }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [filtered, sortCol, sortDir])

  const thCls = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] cursor-pointer select-none whitespace-nowrap hover:text-[rgb(var(--color-text))]'
  const tdCls = 'px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text))] whitespace-nowrap'

  const panelOpen = !!selectedImportLogId

  return (
    <div className="flex flex-col gap-4">
      <div className="mb-1">
        <h2 className="text-tok-base font-bold text-[rgb(var(--color-text))]">Import Log Viewer</h2>
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-0.5">Review import history and approve eligible closed-period overrides. This screen does not initiate imports.</p>
      </div>

      {errorMessage && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{errorMessage}</div>}

      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Import Logs {importLogs.length > 0 && <span className="ml-1 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">({importLogs.length})</span>}</h3>
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
            <input
              type="text"
              placeholder="Search logs…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] py-1.5 pl-8 pr-3 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
            />
          </div>
          {search && <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{sorted.length} of {importLogs.length}</span>}
        </div>

        <div className="overflow-auto max-h-[calc(100vh-22rem)] rounded-xl border border-[rgb(var(--color-border))]">
          <table className="w-full text-tok-sm">
            <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] z-10">
              <tr>
                {[
                  ['source_filename','Source Filename'],
                  ['file_type','File Type'],
                  ['source_system','Source System'],
                  ['load_status','Status'],
                  ['rows_received','Received'],
                  ['rows_loaded','Loaded'],
                  ['rows_rejected','Rejected'],
                  ['closed_override','Closed Override'],
                ].map(([col, label]) => (
                  <th key={col} className={thCls} onClick={() => toggleSort(col)}>
                    {label}<SortIcon col={col} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan="8" className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">Loading import logs…</td></tr>}
              {!isLoading && sorted.length === 0 && (
                <tr><td colSpan="8" className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">{search ? 'No logs match your search.' : 'No import logs found.'}</td></tr>
              )}
              {!isLoading && sorted.map((row) => (
                <tr
                  key={row.import_log_id}
                  className={`border-t border-[rgb(var(--color-border))] cursor-pointer transition-colors hover:bg-[rgb(var(--color-surface-muted))] ${selectedImportLogId === row.import_log_id ? 'bg-[rgb(var(--color-surface-muted))]' : ''}`}
                  onClick={() => handleSelectImportLog(row)}
                >
                  <td className={`${tdCls} max-w-[200px] truncate font-mono text-tok-xs`} title={row.source_filename}>{row.source_filename}</td>
                  <td className={tdCls}>{row.file_type}</td>
                  <td className={tdCls}>{row.source_system || '—'}</td>
                  <td className={tdCls}>{renderStatusBadge(row.load_status)}</td>
                  <td className={tdCls}>{row.rows_received}</td>
                  <td className={tdCls}>{row.rows_loaded}</td>
                  <td className={tdCls}>{row.rows_rejected}</td>
                  <td className={tdCls}>{renderBoolean(row.closed_period_override)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {hasMore && (
          <button type="button" className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 self-start" onClick={() => loadImportLogs({ cursor: nextCursor, append: true })} disabled={isLoadingMore}>
            {isLoadingMore ? 'Loading more…' : 'Load More'}
          </button>
        )}
      </div>

      {/* Detail + Approval side panel — replaces stacked cards below table */}
      {panelOpen && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => { setSelectedImportLogId(null); setSelectedImportLog(null); setConfirmApproveId(null); setApproveError(null) }} />
          <div className="fixed top-0 right-0 z-40 h-full bg-[rgb(var(--color-surface))] border-l border-[rgb(var(--color-border))] shadow-xl flex flex-col" style={{ width: panelWidth }}>
            {/* Drag handle — S946 */}
            <div {...dragHandleProps} />
            <div className="flex items-start justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
              <div>
                <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Import Log Detail</p>
                <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))] truncate max-w-[280px]">{selectedImportLog?.source_filename ?? '…'}</p>
              </div>
              <button onClick={() => { setSelectedImportLogId(null); setSelectedImportLog(null); setConfirmApproveId(null); setApproveError(null) }} className="rounded-lg p-1 hover:bg-[rgb(var(--color-surface-muted))]">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {isLoadingDetail && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading detail…</p>}
              {detailError && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{detailError}</div>}
              {!isLoadingDetail && !detailError && selectedImportLog && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      ['File Type', selectedImportLog.file_type],
                      ['Source System', selectedImportLog.source_system || '—'],
                      ['Load Status', renderStatusBadge(selectedImportLog.load_status)],
                      ['Rows Received', selectedImportLog.rows_received],
                      ['Rows Loaded', selectedImportLog.rows_loaded],
                      ['Rows Rejected', selectedImportLog.rows_rejected],
                      ['Closed Override', renderBoolean(selectedImportLog.closed_period_override)],
                      ['Approved By', selectedImportLog.approved_by || '—'],
                      ['Approved At', selectedImportLog.approved_at || '—'],
                      ['Warnings Count', selectedImportLog.warnings_count],
                      ['Loaded By', selectedImportLog.loaded_by || '—'],
                      ['Loaded At', selectedImportLog.loaded_at || '—'],
                      ['Created At', selectedImportLog.created_at || '—'],
                    ].map(([label, val]) => (
                      <div key={label} className="flex flex-col gap-0.5">
                        <span className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">{label}</span>
                        <span className="text-tok-sm text-[rgb(var(--color-text))]">{val}</span>
                      </div>
                    ))}
                  </div>

                  {selectedImportLog.error_summary && (
                    <div>
                      <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] mb-1">Error Summary</p>
                      <p className="text-tok-sm text-[rgb(var(--color-danger))]">{selectedImportLog.error_summary}</p>
                    </div>
                  )}

                  {selectedImportLog.periods_affected?.length > 0 && (
                    <div>
                      <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] mb-1">Periods Affected</p>
                      {renderPeriodsAffected(selectedImportLog.periods_affected)}
                    </div>
                  )}

                  {/* Approval section — inline in panel */}
                  {canApproveSelectedLog && (
                    <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-3 flex flex-col gap-2">
                      <p className="text-tok-xs font-semibold text-[rgb(var(--color-text))]">Closed Period Override Approval</p>
                      {approveError && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{approveError}</div>}
                      {confirmApproveId !== selectedImportLog.import_log_id ? (
                        <button type="button" className="rounded-lg bg-[rgb(var(--color-warning))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-warning))]/80 disabled:opacity-50 self-start" onClick={() => setConfirmApproveId(selectedImportLog.import_log_id)} disabled={isApproving}>Approve Override</button>
                      ) : (
                        <div className="flex flex-col gap-2">
                          <p className="text-tok-xs text-[rgb(var(--color-warning))]">Confirm approval for this closed-period override. This cannot be undone.</p>
                          <div className="flex gap-2">
                            <button type="button" className="rounded-lg bg-[rgb(var(--color-danger))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-danger))]/80 disabled:opacity-50" onClick={handleApproveOverride} disabled={isApproving}>{isApproving ? 'Approving…' : 'Confirm Approve'}</button>
                            <button type="button" className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50" onClick={() => { setConfirmApproveId(null); setApproveError(null) }} disabled={isApproving}>Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
